import isEqual from 'lodash/isEqual';
import { Plugin, Context } from '@nuxt/types';

import Kilkaya from '@analytics/trackers/Kilkaya/Kilkaya';
import { KilkayaAttributes } from '@analytics/trackers/Kilkaya/types/Kilkaya';
import { Data as ArticleData } from '@root/modules/article/types/article';
import buildPictureUrl from '@core/utils/buildPictureUrl';
import { getEnv } from '@root/common/utils/getEnv';
import { PianoState } from '@piano/types/pianoState';
import { State as ArticleState } from '@root/modules/article/types/article';

const kilkaya: Plugin = async (ctx) => {
  const { app, store } = ctx;
  const { locale, application, name } = app.$channelConfig('settings');

  const kilkaya = new Kilkaya();
  await kilkaya.init();

  /* Attributes getters */
  function getPageTitle(route: Context['route']) {
    const titleParts = [name, route.name];

    if (route.params?.id) {
      titleParts.push(route.params.id as string);
    }

    return titleParts.join(' - ');
  }

  function getContentTags(article: ArticleData): string[] | undefined {
    const relatedEntities = (article as ArticleData).relatedEntities;
    const settings = (article as ArticleData).settings;

    const tags: KilkayaAttributes['contentTag'] = [];

    if (relatedEntities?.hasAudio) {
      tags.push('hasAudio');
    }

    if (relatedEntities?.hasGallery) {
      tags.push('hasGallery');
    }

    if (relatedEntities?.hasStream) {
      tags.push('hasStream');
    }

    if (relatedEntities?.hasVideo) {
      tags.push('hasVideo');
    }

    if (settings?.trending) {
      tags.push('isTrending');
    }

    return tags.length ? Array.from(new Set(tags)) : undefined;
  }

  function getPaywallAttribute(activeArticle: ArticleData) {
    if (activeArticle.content.paywall.enabled) {
      return 'hard';
    }

    // For paywall enabled articles, check channel access
    if (activeArticle.content.paywall.enabled && store.state.piano.access.channelAccess) {
      return 'open';
    }

    return 'open';
  }

  function getKilkayaDefaultPageAttributes(route: Context['route']) {
    const applicationType = application.type;

    return {
      login: Number(store.state.piano.isLoggedIn),
      subscriber: Number(store.state.piano.access.channelAccess),
      url: location.href,
      referer: document.referrer,
      locale,
      title: getPageTitle(route),
      ...(applicationType && { channel: applicationType }),
    };
  }

  function getKilkayaArticleAttributes(activeArticle: ArticleData): KilkayaAttributes {
    const image = activeArticle.metaImage?.id
      ? buildPictureUrl({
          id: activeArticle.metaImage?.id,
          cropperData: {
            w: 1280,
            h: 720,
            ...activeArticle.metaImage?.attributes?.cropperData,
            ...activeArticle.metaImage?.attributes?.cropperData?.customCrops?.find((customCrop: { ratio: string }) => customCrop.ratio === '16:9'),
          },
          url: getEnv('mediaApiCropper'),
        })
      : undefined;

    return {
      paid: Number(activeArticle.content.paywall.enabled),
      paywall: getPaywallAttribute(activeArticle),
      title: activeArticle.content.title.text,
      image,
      type: 'article',
      section: activeArticle.primaryCategory.channel.name,
      subsection: activeArticle.primaryCategory.name,
      author: activeArticle.authors.items.map((author) => author.name),
      tag: activeArticle.tags.items.map((tag) => tag.name),
      publishtime: activeArticle.publishAt,
      modifiedtime: activeArticle.updatedAt,
      contentTag: getContentTags(activeArticle),
      titleComment: activeArticle.content.subtitle?.text || undefined,
      comments: activeArticle.statistics.comments,
    };
  }

  function getKilkayaArticlePageAttributes(activeArticle: ArticleData, route: Context['route']): KilkayaAttributes {
    return {
      ...getKilkayaDefaultPageAttributes(route),
      ...getKilkayaArticleAttributes(activeArticle),
      url: store.state.article.activeArticlePath ? new URL(store.state.article.activeArticlePath, location.origin).href : location.href,
    };
  }

  /* Page view dispatcher */
  function trackPageByType(route: Context['route'], activeArticle?: ArticleData) {
    if (!app.router) {
      return;
    }

    let attributes;

    switch (route.name) {
      case 'article':
        if (activeArticle) {
          attributes = getKilkayaArticlePageAttributes(activeArticle, route);
        }
        break;
      case 'articleComments':
        break;
      default:
        attributes = getKilkayaDefaultPageAttributes(route);
        break;
    }

    if (attributes) {
      kilkaya.pageView(attributes);
    }
  }

  /* Event listeners and watchers */
  // Track page view when in-app navigation happens
  app.router!.afterEach((to, from) => {
    if (!from.name) {
      return;
    }

    const navigationFromArticle = from.name === 'article' || from.name === 'articleComments';
    const navigationToArticle = to.name === 'article' || to.name === 'articleComments';
    const articleToArticleNavigation = navigationFromArticle && navigationToArticle;

    if (!articleToArticleNavigation && store.state.piano.isScriptInited) {
      trackPageByType(to, store.state.article.activeArticle);
    }
  });

  // Track article page view when user opens article or scrolls to the next article
  store.watch(
    (state: { article: ArticleState }) => state.article.activeArticlePath,
    async (activeArticlePath: ArticleState['activeArticlePath']) => {
      if (activeArticlePath && app.router && store.state.article.activeArticle && store.state.piano.isScriptInited) {
        const routeName = app.router.currentRoute.name;
        if (routeName === 'article' || routeName === 'issue-article') {
          trackPageByType(app.router.currentRoute, store.state.article.activeArticle);
        }
      }
    }
  );

  // Track page view after Piano is initialized
  store.watch(
    (state: { piano: PianoState }) => state.piano.isScriptInited,
    (isScriptInited: PianoState['isScriptInited']) => {
      if (!isScriptInited) {
        return;
      }

      watchPianoAccess();
      watchPianoLoginState();
    },
    { immediate: true }
  );

  // Track page view when Piano login state changes
  function watchPianoLoginState() {
    store.watch(
      (state: { piano: PianoState }) => state.piano.isLoggedIn,
      async () => {
        trackPageByType(app.router!.currentRoute, store.state.article.activeArticle);
      }
    );
  }

  // Track page view when Piano access changes
  function watchPianoAccess() {
    store.watch(
      (state: { piano: PianoState }) => state.piano.access,
      async (newAccess: PianoState['access'], oldAccess: PianoState['access']) => {
        if (isEqual(newAccess, oldAccess)) {
          return;
        }

        trackPageByType(app.router!.currentRoute, store.state.article.activeArticle);
      },
      { immediate: true }
    );
  }
};

export default kilkaya;
